import { z } from "zod";

import {
  isoUtcSchema,
  labelIdSchema,
  mailboxIdSchema,
  messageIdSchema,
  threadIdSchema,
  ulidSchema,
} from "./common.js";
import { systemFolderSchema, messageSummarySchema } from "./message.js";
import { draftIdSchema } from "./common.js";

const eventBase = {
  /** Monotonic per-mailbox sequence; lets clients detect gaps and resync. */
  seq: z.number().int().nonnegative(),
  at: isoUtcSchema,
};

/** Events broadcast by the `MailboxRoom` Durable Object. */
export const mailboxEventSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("message.created"),
    mailboxId: mailboxIdSchema,
    message: messageSummarySchema,
    ...eventBase,
  }),
  z.object({
    type: z.literal("message.updated"),
    mailboxId: mailboxIdSchema,
    message: messageSummarySchema,
    ...eventBase,
  }),
  z.object({
    type: z.literal("message.flags_changed"),
    mailboxId: mailboxIdSchema,
    messageIds: z.array(messageIdSchema),
    isRead: z.boolean().nullable(),
    isFlagged: z.boolean().nullable(),
    folder: systemFolderSchema.nullable(),
    ...eventBase,
  }),
  z.object({
    type: z.literal("message.deleted"),
    mailboxId: mailboxIdSchema,
    messageIds: z.array(messageIdSchema),
    ...eventBase,
  }),
  z.object({
    type: z.literal("label.changed"),
    mailboxId: mailboxIdSchema,
    labelId: labelIdSchema,
    deleted: z.boolean(),
    ...eventBase,
  }),
  z.object({
    type: z.literal("draft.changed"),
    mailboxId: mailboxIdSchema,
    draftId: draftIdSchema,
    deleted: z.boolean(),
    ...eventBase,
  }),
  z.object({
    type: z.literal("sync.required"),
    mailboxId: mailboxIdSchema,
    reason: z.string().max(256),
    ...eventBase,
  }),
]);
export type MailboxEvent = z.infer<typeof mailboxEventSchema>;

/** Messages a client may send over the WebSocket. */
export const wsClientMessageSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("ping"), nonce: z.string().max(64).optional() }),
  z.object({ type: z.literal("subscribe"), mailboxId: mailboxIdSchema }),
  z.object({ type: z.literal("unsubscribe"), mailboxId: mailboxIdSchema }),
  z.object({ type: z.literal("ack"), seq: z.number().int().nonnegative() }),
]);
export type WsClientMessage = z.infer<typeof wsClientMessageSchema>;

/** Messages the server may send over the WebSocket. */
export const wsServerMessageSchema = z.union([
  mailboxEventSchema,
  z.object({ type: z.literal("pong"), nonce: z.string().max(64).optional(), at: isoUtcSchema }),
  z.object({
    type: z.literal("ready"),
    mailboxId: mailboxIdSchema,
    seq: z.number().int().nonnegative(),
    at: isoUtcSchema,
  }),
  z.object({ type: z.literal("error"), code: z.string(), message: z.string(), at: isoUtcSchema }),
]);
export type WsServerMessage = z.infer<typeof wsServerMessageSchema>;

/** Client-side connection descriptor persisted for resumable subscriptions. */
export const wsSubscriptionSchema = z.object({
  id: ulidSchema,
  mailboxId: mailboxIdSchema,
  threadId: threadIdSchema.nullable(),
  lastSeq: z.number().int().nonnegative(),
});
export type WsSubscription = z.infer<typeof wsSubscriptionSchema>;
